import { TwitchIcon } from 'react-twitch-implicit-auth';
import PropTypes from 'prop-types';
import Button from '@components/buttons/button.jsx';
import { useEffect, useState } from 'preact/hooks';
import useTwitchConfig from '@hooks/useTwitchConfig.js';
import useTwitchWebhooks from '@hooks/useTwitchWebhooks.js';
import { useTwitchCampaigns } from '@hooks/useTwitchCampaigns.js';

TwitchSetup.propTypes = {
  onSignIn: PropTypes.func,
  onSignOut: PropTypes.func,
  tokenValidated: PropTypes.func,
  redirectURI: PropTypes.string.isRequired,
  lambdaURI: PropTypes.string.isRequired,
};

export default function TwitchSetup({
  onSignIn = ({ username, refreshToken, accessToken }) =>
    console.warn('onSignIn not handled', username, refreshToken, accessToken),
  onSignOut = () => console.warn('onSignOut not handled'),
  tokenValidated = (_) => {},
  redirectURI,
  lambdaURI,
}) {
  const config = useTwitchConfig();
  // TODO: remove this default
  const clientID =
    import.meta.env.VITE_TWITCH_CLIENT_ID || 'ke75dneb44ofatsiqfik7gatsb9qi4';

  const [tokenWasValidated, setTokenWasValidated] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const twitchConfig = useTwitchConfig();

  const { deleteCampaign } = useTwitchCampaigns(config, !tokenWasValidated);

  function messageListener(event) {
    if (
      event.origin !== window.origin ||
      !event.source?.location?.pathname ||
      event.source?.location?.pathname !== '/chatbot-landing'
    )
      return;
    try {
      handleCode(event.data);
    } catch (err) {
      console.error('Error parsing message data', event.data);
    }
  }

  const { getWebhooks, deleteWebhooks, createWebhooks } = useTwitchWebhooks({
    ...config,
    tokenValidated: tokenWasValidated,
  });

  useEffect(() => {
    if (tokenValidated)
      getWebhooks().then((result) => {
        /**
         * @type {Object|null} webhooks
         */
        const webhooks = result;

        if (webhooks !== null && Object.keys(webhooks).length === 0) {
          createWebhooks();
        }
      });
  }, [tokenWasValidated]);

  useEffect(() => {
    window.addEventListener('message', messageListener);
    return () => {
      window.removeEventListener('message', messageListener);
    };
  });

  function handleCode({ code }) {
    const url = `${lambdaURI}/getUserToken`;
    const body = {
      redirectURI,
      code,
    };

    return handleAuthRequest(url, body);
  }

  function refreshToken() {
    setIsSigningIn(true);
    const refreshToken = twitchConfig.twitchRefreshToken;
    const url = `${lambdaURI}/refreshUserToken`;
    const body = {
      refreshToken,
    };

    return handleAuthRequest(url, body);
  }

  function handleAuthRequest(url, body) {
    return fetch(url, {
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
      method: 'POST',
    })
      .then((response) => response.json())
      .then((data) => {
        /**
         * @type {Object} data
         * @param {string} data.access_token
         * @param {string} data.refresh_token
         * @param {Object} data.user
         * @param {string} data.user.login
         * @param {string} data.user.id
         */
        if (data && data.access_token) {
          const accessToken = data.access_token;
          const refreshToken = data.refresh_token;
          let username;
          let broadcasterID;

          if (data.user) {
            if (data.user.login) username = data.user.login;
            if (data.user.id) broadcasterID = data.user.id;
          }

          onTwitchSignIn(true, {
            username,
            accessToken,
            refreshToken,
            broadcasterID,
          });

          return true;
        } else {
          onTwitchSignIn(false);
          return false;
        }
      });
  }

  function validateToken(accessToken) {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${accessToken}`);

    let didSucceed = false;
    return fetch('https://id.twitch.tv/oauth2/validate', {
      headers,
      method: 'GET',
    })
      .then((response) => {
        didSucceed = response.ok;
        return response.json();
      })
      .catch((error) => {
        console.error(error);
        return null;
      })
      .then(async (data) => {
        if (data && didSucceed) {
          tokenValidated(true);
          setTokenWasValidated(true);
          return true;
        } else if (localStorage.getItem('twitchRefreshToken').length > 0) {
          return await refreshToken().then((didRefresh) => {
            tokenValidated(didRefresh);
            setTokenWasValidated(didRefresh);
            return didRefresh;
          });
        } else {
          tokenValidated(false);
          setTokenWasValidated(false);
          return false;
        }
      });
  }

  function onTwitchSignIn(didSignIn, data = null) {
    if (didSignIn && data) {
      const { username, accessToken, refreshToken, broadcasterID } = data;
      config.twitchAccessToken = accessToken;
      config.twitchRefreshToken = refreshToken;
      config.twitchUsername = username;
      config.twitchBroadcasterID = broadcasterID;
      localStorage.setItem('twitchAccessToken', accessToken);
      localStorage.setItem('twitchRefreshToken', refreshToken);
      localStorage.setItem('twitchUsername', username);
      localStorage.setItem('twitchBroadcasterID', broadcasterID);
      setIsSignedIn(true);
      setIsSigningIn(false);
      tokenValidated(didSignIn);
      setTokenWasValidated(didSignIn);
      onSignIn({ username, refreshToken, accessToken });
    } else {
      console.warn('Unable to get token');
      setIsSigningIn(false);
      setIsSignedIn(false);
      setTokenWasValidated(false);
    }
  }

  function signOutTwitch() {
    deleteWebhooks();

    if (config.twitchCampaignID) {
      deleteCampaign(config.twitchCampaignID).then();
    }

    config.twitchAccessToken = '';
    config.twitchRefreshToken = '';
    config.twitchUsername = '';
    config.twitchBroadcasterID = '';
    config.twitchCampaignID = '';
    config.twitchCampaignGoal = '0.00';
    config.twitchCampaignName = '';
    localStorage.removeItem('twitchAccessToken');
    localStorage.removeItem('twitchRefreshToken');
    localStorage.removeItem('twitchUsername');
    localStorage.removeItem('twitchBroadcasterID');
    localStorage.removeItem('twitchCampaignID');
    localStorage.removeItem('twitchCampaignGoal');
    localStorage.removeItem('twitchCampaignName');
    setIsSignedIn(false);
    setTokenWasValidated(false);
    onSignOut();
  }

  function openURL() {
    const windowProxy = window.open(connectURL);
    windowProxy.focus();
    setIsSigningIn(true);
    setTimeout(() => setIsSigningIn(false), 60000);
  }

  if (
    !isSignedIn &&
    !isSigningIn &&
    twitchConfig.twitchUsername &&
    twitchConfig.twitchAccessToken &&
    twitchConfig.twitchRefreshToken
  ) {
    setIsSigningIn(true);
    validateToken(twitchConfig.twitchAccessToken).then((isValid) => {
      setIsSigningIn(false);
      if (isValid) {
        setIsSignedIn(true);
        onSignIn({
          username: twitchConfig.twitchUsername,
          refreshToken: twitchConfig.twitchRefreshToken,
          accessToken: twitchConfig.twitchAccessToken,
          broadcasterID: twitchConfig.twitchBroadcasterID,
        });
      } else {
        signOutTwitch();
      }
    });
  }

  const scopes = [
    'user_read',
    'channel:read:charity',
    'chat:read',
    'chat:edit',
    'channel:bot',
    'user:write:chat',
    'channel:manage:moderators',
  ];
  const urlParams = {
    client_id: clientID,
    redirect_uri: redirectURI,
    response_type: 'code',
    scope: scopes.join(' '),
  };

  const connectURL = new URL('https://id.twitch.tv/oauth2/authorize');

  Object.keys(urlParams).forEach((key) => {
    connectURL.searchParams.set(key, urlParams[key]);
  });
  let twitchBlock;

  if (isSignedIn)
    twitchBlock = (
      <div className="flex items-center justify-between">
        <article className="prose text-sm">
          Your Twitch account, <b>{config.twitchUsername}</b>, has been
          connected
        </article>
        <Button
          style="secondary"
          mode="button"
          onClick={signOutTwitch}
          size="small"
        >
          Sign Out
        </Button>
      </div>
    );
  else if (!isSigningIn)
    twitchBlock = (
      <button className="btn btn-twitch" onClick={openURL}>
        <TwitchIcon />
        <br />
        <span>Sign in with Twitch</span>
      </button>
    );
  else
    twitchBlock = (
      <button className="btn btn-twitch" onClick={openURL} disabled={true}>
        <span className="loading loading-ring loading-sm"></span>
        <br />
        <span>Signing in with Twitch</span>
      </button>
    );
  return <>{twitchBlock}</>;
}
